import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GlassesRegular } from '@fluentui/react-icons';
import { Box, Button, Card, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { StatusChip } from '@ui/components/common/chips/StatusChip';
import { ConfirmDialog, EditSystemDialog } from '@ui/components/Dialogs';
import useSystemsStyles from './useSystemsStyles';
import SystemPreviewDrawer from '../PreviewDrawer';
import { formatDMY } from '@application/utilities/date-time-formater';
import { SDataGrid2 } from '@ui/components/common/grid/SDataGrid2';
import { useDispatch } from 'react-redux';
import { setSystemEditMode, deleteSystem } from '@application/redux/SystemDetail/system-detail-slice';
import { toast } from 'react-toastify';
const SystemsDataGrid = ({ fullHeight = true, rows, hiddenColumns = [], showFilters, filters, setFilters, setSystemQuery, systemQuery, totalRows, onSystemUpdated }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { systemsGridCardStyles } = useSystemsStyles();
    const [showSystemPreviewDrawer, setShowSystemPreviewDrawer] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedSystemForAction, setSelectedSystemForAction] = useState(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deletedSystemIds, setDeletedSystemIds] = useState([]);
    const [updatedSystems, setUpdatedSystems] = useState(new Map());
    // Apply updates to rows and filter out deleted systems
    const processedRows = (rows || [])
        .filter((row) => !deletedSystemIds.includes(row.id))
        .map((row) => {
        const updatedData = updatedSystems.get(row.id);
        return updatedData ? { ...row, ...updatedData } : row;
    });
    // Reset deleted systems and updated systems when rows change (e.g., when data is refreshed)
    useEffect(() => {
        setDeletedSystemIds([]);
        setUpdatedSystems(new Map());
    }, [rows]);
    const handleCloseIncidentPreviewDrawer = () => {
        setShowSystemPreviewDrawer(false);
    };
    const [systemId, setSystemId] = useState('');
    // Menu handlers
    const handleMenuOpen = (event, system) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedSystemForAction(system);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        // Don't clear selectedSystemForAction here - let individual handlers manage it
    };
    // Edit handlers
    const handleEditClick = () => {
        if (selectedSystemForAction) {
            dispatch(setSystemEditMode(selectedSystemForAction));
            setShowEditDialog(true);
        }
        handleMenuClose();
        // Don't clear selectedSystemForAction here - keep it for the edit dialog
    };
    const handleEditDialogClose = () => {
        setShowEditDialog(false);
        setSelectedSystemForAction(null);
    };
    const handleSystemUpdated = (updatedData) => {
        if (selectedSystemForAction) {
            // Temporarily update the grid row with the new data
            const newMap = new Map(updatedSystems);
            newMap.set(selectedSystemForAction.id, {
                systemName: updatedData.systemName,
                systemType: updatedData.systemType,
                description: updatedData.description,
                cameraUrl: updatedData.cameraUrl,
                jetsonId: updatedData.jetsonId
            });
            setUpdatedSystems(newMap);
        }
        // Call the callback to trigger data refresh in parent component
        if (onSystemUpdated) {
            onSystemUpdated();
        }
    };
    // Delete handlers
    const handleDeleteClick = () => {
        setShowDeleteDialog(true);
        handleMenuClose();
        // Don't clear selectedSystemForAction here - keep it for the delete dialog
    };
    const handleDeleteDialogClose = () => {
        setShowDeleteDialog(false);
        setSelectedSystemForAction(null);
    };
    const handleConfirmDelete = async () => {
        if (selectedSystemForAction) {
            try {
                const response = await dispatch(deleteSystem(selectedSystemForAction.id));
                if (deleteSystem.fulfilled.match(response)) {
                    const { isSuccess, message } = response.payload;
                    if (isSuccess) {
                        // Add the deleted system ID to the local state to hide it from the grid
                        setDeletedSystemIds((prev) => [...prev, selectedSystemForAction.id]);
                        toast.success(message || 'System deleted successfully!');
                    }
                    else {
                        toast.error(message);
                    }
                }
                else if (deleteSystem.rejected.match(response)) {
                    toast.error(response.payload);
                }
            }
            catch (error) {
                toast.error('Failed to delete system');
            }
        }
        handleDeleteDialogClose();
    };
    const columns = [
        {
            field: 'systemName',
            headerName: t('SystemName'),
            minWidth: 150,
            flex: 1,
            editable: false
        },
        {
            field: 'systemType',
            headerName: t('SystemType'),
            minWidth: 150,
            flex: 1,
            editable: false
        },
        {
            field: 'lastLocation',
            headerName: t('LastLocation'),
            minWidth: 150,
            type: 'string',
            flex: 1,
            editable: false
        },
        {
            field: 'lastInfoReceived',
            headerName: t('LastInfoReceived'),
            minWidth: 150,
            flex: 1,
            type: 'dateTime',
            editable: false,
            valueFormatter: (params) => formatDMY(params)
        },
        {
            field: 'asset',
            headerName: t('Asset'),
            minWidth: 150,
            flex: 1,
            editable: false
        },
        {
            field: 'description',
            headerName: t('Description'),
            minWidth: 300,
            flex: 1.5,
            editable: false
        },
        {
            field: 'isActive',
            headerName: 'Status',
            minWidth: 200,
            flex: 0.7,
            editable: false,
            type: 'singleSelect',
            valueOptions: [
                { value: 'Active', label: t('Active') },
                { value: 'InActive', label: t('InActive') }
            ],
            renderCell: (params) => {
                const label = params.value ? 'Active' : 'InActive';
                return _jsx(StatusChip, { status: label });
            }
        },
        {
            field: '',
            headerName: '',
            minWidth: 120,
            display: 'flex',
            align: 'center',
            editable: false,
            sortable: false,
            type: 'actions',
            renderCell: (params) => {
                return (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, pr: '6px' }, children: _jsx(Button, { variant: 'outlined', color: 'info', startIcon: _jsx(GlassesRegular, { fontSize: 20 }), onClick: () => {
                            setSystemId(params.row.id);
                            setShowSystemPreviewDrawer(true);
                        }, children: t('Preview') }) }));
            }
        },
        {
            field: 'actions',
            headerName: '',
            width: 56,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            headerAlign: 'right',
            align: 'right',
            type: 'actions',
            renderCell: (params) => (_jsx(IconButton, { size: 'small', "aria-label": t('Actions') || 'Actions', onClick: (e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, params.row);
                }, children: _jsx(MoreVertIcon, {}) }))
        }
    ];
    return (_jsxs(Fragment, { children: [_jsx(Card, { className: 'systems-grid-card', sx: systemsGridCardStyles, children: _jsx(Box, { className: 'table-container', height: fullHeight ? 'calc(100dvh - 240px)' : 'auto', children: _jsx(SDataGrid2, { rows: processedRows, columns: columns, serverSidePagination: true, loading: false, showFilters: showFilters, hideColumns: hiddenColumns, filters: filters, setFilters: setFilters, pageSizeOptions: [10, 20, 50, 100], initialState: {
                            pagination: {
                                paginationModel: {
                                    page: (systemQuery.pageNumber || 1) - 1,
                                    pageSize: systemQuery.pageSize || 10
                                }
                            }
                        }, onPaginationModelChange: (model) => {
                            setSystemQuery((prev) => ({
                                ...prev,
                                pageNumber: model.page + 1,
                                pageSize: model.pageSize
                            }));
                        }, totalRows: totalRows - deletedSystemIds.length }) }) }), showSystemPreviewDrawer && (_jsx(SystemPreviewDrawer, { open: showSystemPreviewDrawer, onClose: handleCloseIncidentPreviewDrawer, id: systemId })), _jsxs(Menu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, children: [_jsxs(MenuItem, { onClick: handleEditClick, children: [_jsx(ListItemIcon, { children: _jsx(EditOutlinedIcon, { fontSize: 'small' }) }), _jsx(ListItemText, { children: t('Edit') ?? 'Edit' })] }), _jsxs(MenuItem, { onClick: handleDeleteClick, children: [_jsx(ListItemIcon, { children: _jsx(DeleteOutlineOutlinedIcon, { fontSize: 'small' }) }), _jsx(ListItemText, { children: t('Delete') ?? 'Delete' })] })] }), showEditDialog && (_jsx(EditSystemDialog, { isOpen: showEditDialog, onClose: handleEditDialogClose, onSystemUpdated: handleSystemUpdated })), showDeleteDialog && selectedSystemForAction && (_jsx(ConfirmDialog, { isOpen: showDeleteDialog, onClose: handleDeleteDialogClose, onConfirm: handleConfirmDelete, actionText: t('Delete System with name'), screenName: selectedSystemForAction.systemName || 'Unknown System', variant: 'delete' }))] }));
};
export default SystemsDataGrid;
